import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Container } from "@/components/shared";
import { ILLUSTRATIONS } from "@/lib/profiles";
import KidsBookshelf from "@/components/KidsBookshelf";
import ChildWishes from "@/components/ChildWishes";
import { Mic, Search, Scale, Compass, LifeBuoy, ArrowRight, Ear, FolderCheck, MessageSquareWarning, HandHeart, BookOpen, ChevronDown } from "lucide-react";

const CARDS = [
  { id: "understand", to: "/situation", icon: Compass },
  { id: "rights", to: "/rights", icon: Scale },
  { id: "act", to: "/courriers", icon: LifeBuoy },
  { id: "resources", to: "/bibliotheque", icon: Search },
  { id: "sorties", to: "/carte-kids", icon: Compass },
];

const STEPS = [
  { n: 1, icon: Ear }, { n: 2, icon: FolderCheck }, { n: 3, icon: MessageSquareWarning }, { n: 4, icon: HandHeart },
];

const GUIDE_META = [
  { id: "listen", icon: Ear }, { id: "proof", icon: FolderCheck }, { id: "steps", icon: MessageSquareWarning },
];

export default function ParentHome() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [openGuide, setOpenGuide] = useState(null);
  const rise = { hidden: { opacity: 0, y: 16 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.5 } }) };

  return (
    <Container className="max-w-4xl">
      <div data-testid="parent-home">
        {/* Accueil chaleureux */}
        <motion.section initial="hidden" animate="show" variants={rise} custom={0}
          className="card-surface overflow-hidden mb-8 flex flex-col sm:flex-row items-stretch">
          <div className="p-6 md:p-8 flex-1">
            <h1 className="font-display text-3xl md:text-4xl" style={{ color: "var(--accent-strong)" }}>{t("parent.welcome")}</h1>
            <p className="text-base mt-3 max-w-md" style={{ color: "var(--text-main)" }}>
              {t("parent.welcomeSub")}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button data-testid="parent-cta-talk" onClick={() => navigate("/situation")}
                className="gold-gradient rounded-lg px-5 py-3 font-semibold inline-flex items-center gap-2"
                style={{ color: "var(--accent-fg)" }}>
                <Mic size={18} /> {t("parent.talkToAI")}
              </button>
              <button data-testid="parent-cta-child" onClick={() => navigate("/analyze")}
                className="rounded-lg px-5 py-3 font-semibold inline-flex items-center gap-2 border transition-colors"
                style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
                <Search size={18} /> {t("parent.childSituation")}
              </button>
            </div>
          </div>
          <img src={ILLUSTRATIONS.parentHero} alt="" className="w-full sm:w-56 h-48 sm:h-auto object-cover shrink-0" data-testid="parent-hero-img" />
        </motion.section>

        {/* Que souhaitez-vous faire ? */}
        <ChildWishes />
        <h2 className="font-display text-xl mb-4" style={{ color: "var(--text-main)" }}>{t("parent.whatToDo")}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {CARDS.map((c, i) => (
            <motion.button key={c.id} data-testid={`parent-card-${c.id}`} onClick={() => navigate(c.to)}
              initial="hidden" animate="show" variants={rise} custom={i + 1}
              className="card-surface p-5 text-left flex items-start gap-4 group">
              <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent)" }}>
                <c.icon size={20} />
              </span>
              <div className="flex-1">
                <p className="font-display text-base" style={{ color: "var(--text-main)" }}>{t(`parent.cards.${c.id}.title`)}</p>
                <p className="text-white/60 text-sm mt-0.5">{t(`parent.cards.${c.id}.desc`)}</p>
              </div>
              <ArrowRight size={16} className="text-gold mt-1 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          ))}
        </div>

        {/* Des livres pour vos enfants */}
        <KidsBookshelf variant="parent" />

        {/* Guides pratiques */}
        <div className="flex items-center gap-2 mb-4">
          <BookOpen size={20} className="text-gold" />
          <h2 className="font-display text-xl" style={{ color: "var(--text-main)" }}>{t("parent.guidesTitle")}</h2>
        </div>
        <div className="space-y-3 mb-10" data-testid="parent-guides">
          {GUIDE_META.map((g, i) => {
            const open = openGuide === g.id;
            const points = t(`parent.guides.${g.id}.points`, { returnObjects: true });
            return (
              <motion.div key={g.id} initial="hidden" animate="show" variants={rise} custom={i + 1}
                className="card-surface overflow-hidden">
                <button data-testid={`parent-guide-${g.id}`} onClick={() => setOpenGuide(open ? null : g.id)}
                  className="w-full p-4 flex items-center gap-3 text-left">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: "color-mix(in srgb, var(--accent) 12%, transparent)", color: "var(--accent)" }}>
                    <g.icon size={18} />
                  </span>
                  <span className="flex-1 font-display text-base" style={{ color: "var(--text-main)" }}>{t(`parent.guides.${g.id}.title`)}</span>
                  <ChevronDown size={18} className="text-gold transition-transform" style={{ transform: open ? "rotate(180deg)" : "none" }} />
                </button>
                <AnimatePresence initial={false}>
                  {open && (
                    <motion.ul key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="px-4 pb-4 space-y-2.5" data-testid={`parent-guide-body-${g.id}`}>
                      {(Array.isArray(points) ? points : []).map((p, k) => (
                        <li key={k} className="flex gap-2.5 text-sm" style={{ color: "color-mix(in srgb, var(--text-main) 78%, transparent)" }}>
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--accent)" }} />
                          <span>{p}</span>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Étapes recommandées */}
        <h2 className="font-display text-xl mb-4" style={{ color: "var(--text-main)" }}>{t("parent.recoTitle")}</h2>
        <div className="space-y-3">
          {STEPS.map((s, i) => (
            <motion.div key={s.n} data-testid={`parent-step-${s.n}`}
              initial="hidden" animate="show" variants={rise} custom={i + 5}
              className="card-surface p-4 flex items-center gap-4">
              <span className="w-9 h-9 rounded-full flex items-center justify-center font-display text-base shrink-0"
                style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>{s.n}</span>
              <s.icon size={20} className="text-gold shrink-0" />
              <div>
                <p className="font-medium" style={{ color: "var(--text-main)" }}>{t(`parent.steps.s${s.n}.title`)}</p>
                <p className="text-white/60 text-sm">{t(`parent.steps.s${s.n}.desc`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Container>
  );
}
